import React, { Suspense, useEffect, useState } from 'react';
import { Facebook as LoadingState } from 'react-content-loader';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Tag from 'components/Tag';
import Icon from 'components/icons/Icon';
import SectionWrapper from 'pages/Layout/SectionWrapper';
import GeneralDataBox from 'components/boxes/GeneralDataBox';
import EmptyStateBox from 'components/boxes/EmptyStateBox/EmptyStateBox';
import {
  SectionHeader,
  EngagementGrid,
  EngagementCard,
  AveragesSection,
  AveragesGrid,
  PostsSection,
  PostsGrid,
  PostCard
} from './Styled';

import { selectUser } from 'redux/slices/userSlice';
import {
  selectPerformance,
  setPerformance
} from 'redux/slices/PerformanceSection/PerformanceSectionSlice';
import { getAmbassadorPerformance } from 'services/rest/PerformanceSection/PerformanceSection';
import { useFetch } from 'hooks/useFetch.hook';
import { getInfoUser } from 'services/rest/User/getInfoUser';
import { nFormatter } from 'helpers/formatHelper';

const InstagramSection = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['instagram_section', 'performance_section']);
  const { currentUser } = useSelector(selectUser);
  const { performanceSection } = useSelector(selectPerformance);
  const [localLoading, setLocalLoading] = useState(true);
  const [posts, setPosts] = useState<any[]>([]);
  const { data } = useFetch(
    getInfoUser(currentUser?.user?.id, currentUser?.token, currentUser?.role)
  );
  const userData = data as any;

  const getData = async () => {
    try {
      const { data } = await getAmbassadorPerformance(currentUser?.token);
      dispatch(setPerformance(data.data));
      const album = data?.data?.dataInstagram?.albumInstagram;
      if (album && Array.isArray(album)) {
        setPosts(album);
      }
    } catch {
      // Silently handle
    } finally {
      setLocalLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  if (localLoading || !userData) {
    return <LoadingState height="230px" width="100%" />;
  }

  if (!userData?.linkedInstagram) {
    return (
      <SectionWrapper>
        <SectionHeader>
          <Tag network="instagram" variant="network" />
          <h1>{t('instagram_section:title', 'My Instagram Activity')}</h1>
        </SectionHeader>
        <EmptyStateBox
          title={t(
            'instagram_section:connect_account',
            'Connect your Instagram account to see your activity'
          )}
          description={t(
            'instagram_section:connect_account_description',
            'Link your Instagram Business account to track your posts, reach and engagement'
          )}
        />
      </SectionWrapper>
    );
  }

  const igData = performanceSection?.dataInstagram;

  const summaryItems = [
    {
      icon: <Icon name="report" size="big" />,
      value: performanceSection?.postsByNetwork?.instagram
        ? performanceSection.postsByNetwork.instagram.toString()
        : '0',
      text: t('instagram_section:total_posts', 'Posts published')
    },
    {
      icon: <Icon name="total-reach" size="big" />,
      value: nFormatter(performanceSection?.potentialReach),
      text: t('instagram_section:potential_reach', 'Potential reach')
    },
    {
      icon: <Icon name="star" size="big" />,
      value: performanceSection?.annualRanking?.totalPoints
        ? performanceSection.annualRanking.totalPoints.toString()
        : '0',
      text: t('instagram_section:points_earned', 'Points earned')
    }
  ];

  return (
    <Suspense fallback={<LoadingState height="230px" width="100%" />}>
      <SectionWrapper>
        <SectionHeader>
          <Tag network="instagram" variant="network" />
          <h1>{t('instagram_section:title', 'My Instagram Activity')}</h1>
        </SectionHeader>

        <GeneralDataBox items={summaryItems} />

        <EngagementGrid>
          <EngagementCard>
            <p className="value">{nFormatter(igData?.totalLikes)}</p>
            <p className="label">
              {t('instagram_section:total_likes', 'Total Likes')}
            </p>
          </EngagementCard>
          <EngagementCard>
            <p className="value">{nFormatter(igData?.totalComments)}</p>
            <p className="label">
              {t('instagram_section:total_comments', 'Total Comments')}
            </p>
          </EngagementCard>
          <EngagementCard>
            <p className="value">{nFormatter(igData?.totalShares)}</p>
            <p className="label">
              {t('instagram_section:total_shares', 'Total Shares')}
            </p>
          </EngagementCard>
          <EngagementCard>
            <p className="value">{nFormatter(igData?.totalReach)}</p>
            <p className="label">
              {t('instagram_section:total_reach', 'Total Reach')}
            </p>
          </EngagementCard>
          <EngagementCard>
            <p className="value">{nFormatter(igData?.totalViews)}</p>
            <p className="label">
              {t('instagram_section:total_views', 'Total Views')}
            </p>
          </EngagementCard>
          <EngagementCard>
            <p className="value">{nFormatter(igData?.totalSaved)}</p>
            <p className="label">
              {t('instagram_section:total_saved', 'Total Saved')}
            </p>
          </EngagementCard>
        </EngagementGrid>

        <AveragesSection>
          <h2>{t('instagram_section:averages_title', 'Average per Post')}</h2>
          <AveragesGrid>
            <EngagementCard>
              <p className="value">{igData?.averageLikes || '0'}</p>
              <p className="label">
                {t('instagram_section:avg_likes', 'Avg. Likes')}
              </p>
            </EngagementCard>
            <EngagementCard>
              <p className="value">{igData?.averageComments || '0'}</p>
              <p className="label">
                {t('instagram_section:avg_comments', 'Avg. Comments')}
              </p>
            </EngagementCard>
            <EngagementCard>
              <p className="value">{igData?.averageShares || '0'}</p>
              <p className="label">
                {t('instagram_section:avg_shares', 'Avg. Shares')}
              </p>
            </EngagementCard>
          </AveragesGrid>
        </AveragesSection>

        <PostsSection>
          <h2>{t('instagram_section:recent_posts', 'Recent Posts')}</h2>
          {posts.length > 0 ? (
            <PostsGrid>
              {posts.map((post: any, index: number) => (
                <PostCard
                  key={index}
                  href={post.permalinkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {post.url ? (
                    <img
                      className="post-image"
                      src={post.url}
                      alt="Instagram post"
                    />
                  ) : (
                    <div className="post-placeholder">
                      <Icon name="instagram" size="big" />
                    </div>
                  )}
                  <div className="post-body">
                    {post.isVideo && <span className="post-badge">Reel</span>}
                  </div>
                  <div className="post-metrics">
                    <div className="post-metric">
                      <span>{nFormatter(post.postLikes)}</span> likes
                    </div>
                    <div className="post-metric">
                      <span>{nFormatter(post.postComments)}</span> comments
                    </div>
                    <div className="post-metric">
                      <span>{nFormatter(post.postShares)}</span> shares
                    </div>
                    <div className="post-metric">
                      <span>{nFormatter(post.postReach)}</span> reach
                    </div>
                    <div className="post-metric">
                      <span>{nFormatter(post.postViews)}</span> views
                    </div>
                    <div className="post-metric">
                      <span>{nFormatter(post.postSaved)}</span> saved
                    </div>
                  </div>
                </PostCard>
              ))}
            </PostsGrid>
          ) : (
            <EmptyStateBox
              title={t('instagram_section:no_posts', 'No posts available yet')}
              description={t(
                'instagram_section:no_posts_description',
                'Your recent Instagram posts will appear here'
              )}
            />
          )}
        </PostsSection>
      </SectionWrapper>
    </Suspense>
  );
};

export default InstagramSection;
